/**
 * V2OptionsListSection - Editor section for V2 theme options list settings
 *
 * Replaces V1 TableSettingsSection. Uses V2 types:
 * - ToggleMarkKind ('square'|'circle') instead of string union
 * - Single listBorderRadius (V2 uses uniform corner radius)
 * - Item title/subtitle as TypographyStyle instead of LabelBlock
 *
 * Applies to V2 StepKinds: ListMultiPick, ListSinglePick, GridMultiPick,
 * GridSinglePick, HeroList.
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
  TextField,
  Autocomplete,
} from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import { ColorInput } from '@modules/reactFlow/components/shared/styleFields/ColorInput';
import {
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  NUMBER_0_50_OPTIONS,
} from '@modules/reactFlow/config/atomicFields/options';
import type { ThemeV2OptionsListSettings, TypographyStyle } from '../../types/themesV2.types';
import { useFontLabels } from '@modules/font-config/context/FontLabelsContext';

export interface V2OptionsListSectionProps {
  /** Current options list settings */
  optionsListSettings: ThemeV2OptionsListSettings;
  /** Callback when setting changes */
  onChange: (path: string, value: unknown) => void;
  /** Whether section is disabled */
  disabled?: boolean;
  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
}

/**
 * V2OptionsListSection - SAccordion-based options list settings editor
 */
export const V2OptionsListSection: React.FC<V2OptionsListSectionProps> = ({
  optionsListSettings,
  onChange,
  disabled = false,
  defaultExpanded = false,
}) => {
  const fontLabels = useFontLabels();

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange('optionsListSettings.checkboxKind', event.target.value);
    },
    [onChange]
  );

  const handleSliderChange = useCallback(
    (_event: Event, value: number | number[]) => {
      onChange('optionsListSettings.listBorderRadius', Array.isArray(value) ? value[0] : value);
    },
    [onChange]
  );

  const handleSelectChange = useCallback(
    (path: string) => (event: { target: { value: unknown } }) => {
      onChange(path, event.target.value);
    },
    [onChange]
  );

  const handleNumberComboChange = useCallback(
    (path: string) => (value: string | number | null) => {
      if (value != null) {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(num)) {
          onChange(path, num);
        }
      }
    },
    [onChange]
  );

  const handleColorChange = useCallback(
    (path: string) => (value: unknown) => {
      onChange(path, value);
    },
    [onChange]
  );

  // Render TypographyStyle fields for item title/subtitle
  const renderTypographyFields = (prefix: string, style: TypographyStyle, label: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Font Family */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id={`${prefix}-family-label`}>Font Family</InputLabel>
          <Select
            labelId={`${prefix}-family-label`}
            value={style.fontFamily ?? 'SF Pro'}
            onChange={handleSelectChange(`${prefix}.fontFamily`)}
            label="Font Family"
            disabled={disabled}
            aria-label={`${label} Font Family`}
          >
            {FONT_FAMILY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {fontLabels[opt.value] || opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Font Weight - Autocomplete with freeSolo */}
        <Box sx={{ width: 80 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Font weight
          </Typography>
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            disabled={disabled}
            options={FONT_WEIGHT_OPTIONS_SHORT.map((o) => o.value)}
            value={String(style.fontWeight ?? 400)}
            onChange={(_, newValue) => handleNumberComboChange(`${prefix}.fontWeight`)(newValue)}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && newValue) {
                handleNumberComboChange(`${prefix}.fontWeight`)(newValue);
              }
            }}
            renderInput={(params) => <TextField {...params} variant="standard" />}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Font Size - Autocomplete with freeSolo */}
        <Box sx={{ width: 70 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Font size
          </Typography>
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            disabled={disabled}
            options={NUMBER_0_50_OPTIONS.map((o) => o.value)}
            value={String(style.fontSize ?? 15)}
            onChange={(_, newValue) => handleNumberComboChange(`${prefix}.fontSize`)(newValue)}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && newValue) {
                handleNumberComboChange(`${prefix}.fontSize`)(newValue);
              }
            }}
            renderInput={(params) => <TextField {...params} variant="standard" />}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Color */}
        <Box sx={{ width: 120 }}>
          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
            Color
          </Typography>
          <ColorInput
            value={style.color ?? '#ffffff'}
            onChange={handleColorChange(`${prefix}.color`)}
            disabled={disabled}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <SAccordion
      title="Options List"
      collapsible
      defaultExpanded={defaultExpanded}
      disabled={disabled}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Checkbox Kind (ToggleMarkKind) */}
        <FormControl component="fieldset" disabled={disabled}>
          <FormLabel component="legend" id="v2-checkbox-kind-label">
            Checkbox Kind
          </FormLabel>
          <RadioGroup
            aria-labelledby="v2-checkbox-kind-label"
            aria-label="Checkbox Kind"
            name="v2-checkboxKind"
            value={optionsListSettings.checkboxKind}
            onChange={handleRadioChange}
            row
          >
            <FormControlLabel value="circle" control={<Radio />} label="Circle" />
            <FormControlLabel value="square" control={<Radio />} label="Square" />
          </RadioGroup>
        </FormControl>

        {/* List Border Radius (single unified value for V2) */}
        <Box>
          <Typography
            component="label"
            id="v2-list-border-radius-label"
            variant="body2"
            sx={{ mb: 1, display: 'block' }}
          >
            List Border Radius
          </Typography>
          <Slider
            aria-labelledby="v2-list-border-radius-label"
            aria-label="List Border Radius"
            value={optionsListSettings.listBorderRadius}
            onChange={handleSliderChange}
            min={0}
            max={32}
            step={1}
            marks
            valueLabelDisplay="auto"
            disabled={disabled}
          />
        </Box>

        {/* Item Title Font Settings (TypographyStyle) */}
        {renderTypographyFields(
          'optionsListSettings.itemTitle',
          optionsListSettings.itemTitle,
          'Item Title'
        )}

        {/* Item Subtitle Font Settings (TypographyStyle) */}
        {renderTypographyFields(
          'optionsListSettings.itemSubtitle',
          optionsListSettings.itemSubtitle,
          'Item Subtitle'
        )}
      </Box>
    </SAccordion>
  );
};

export default V2OptionsListSection;
